import {useRef, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css, keyframes} from '@emotion/react'

import {shuffle} from 'lodash'

import {connect, useDispatch} from 'react-redux'

import {useNavigate, Link} from 'react-router-dom'

import {show} from '../../../apis'

import { 
  makeGetPerformanceById,
  makeGetPerformanceTracks,
  selectPerformanceFetchedAssociations 
} from '../../../selectors'


import {
  play, 
  pause, 
  queue,
  enqueue,
  showAlert,
  getAndQueueTracks,
  addTracksToPerformance
} from '../../../actions'

import {
  // convertSpotifyTracksToCkordFormat,
  convertAppleMusicTracksToCkordFormat
} from '../../../helpers'


import PlayIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import MapListItemDate from './MapListItemDate'
import MapListItemInformation from './MapListItemInformation'



const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px)
  }
  to {
    opacity: 1;
    transform: translateY(0)
  }
`


export const styles = {
  root: css`
    padding: 10px 0px;
    display: flex;

    @media (max-width: 600px) {
      padding: 10px 0px;
    }
  `,
  fadeIn: css`
    opacity: 0;
    transform: translateY(10px);
    animation: ${fadeIn} 300ms ease-in forwards;
  `,
  innerRoot: css`
    display: flex;
    position: relative;
    cursor: pointer;
    height: 210px;
    width: 100%;
    flex-grow: 1;
    align-items: flex-start;

    @media (max-width: 600px) {
      height: 150px;
    }
  `,
  dateContainer: css`
    margin-right: 25px;

    @media (max-width: 600px) {
      margin-right: 7px;
    }
  `,
  imageContainer: css`
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    aspect-ratio: 3 / 4;
    width: 140px;
    min-width: 140px;
    flex: 0 0 auto;
    border-radius: 5px;
    margin-right: 25px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.2), 0 0 5px rgba(0,0,0,0.15);

    img {
      width: 100%;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: 5px;
      overflow: hidden;
    }

    @media (max-width: 600px) {
      width: 100px;
      min-width: 100px;
    }
  `,
  iconContainer: css`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: opacity 0.3s;
    right: -20px;
    bottom: -20px;
  `,
  iconSubContainer: css`
    width: 100%;
    height: 100%;
    position: relative;
  `,
  info: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    width: 100%;
    flex: 1 1 auto;
    overflow: hidden;
  `,
  iconCircle: css`
    position: absolute;
    bottom: 5px;
    right: 10px;
    display: flex;
    width: 50px;
    height: 50px;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background: rgb(255, 0, 149);
    box-shadow: 0px 5px 15px -3px rgba(0,0,0,.3);

    @media (max-width: 600px) {
      width: 46px;
      height: 46px;
    }
  `,
  selected: css`
    transform: scale(1.1);
  `,
  link: css`
    text-decoration: none;
    color: inherit;
  `,
}


const icons = {
  play:  <PlayIcon  color='secondary' fontSize='medium' />,
  pause: <PauseIcon color='secondary' fontSize='medium' />
}


const MapListItem = ({
  performance,
  pause,
  play,
  start,
  queue,
  tracks,
  enqueue,
  isCaller,
  isClicked,
  isPlaying,
  showAlert,
  trackCount,
  imageURL,
  animate,
  externalImageURL,
  externalImageSrcset,
  areTracksFetched,
  getAndQueueTracks,
  addTracksToPerformance
}) => {

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const didRunRef = useRef(false)
  const containerRef = useRef()


  const href = `/performances/${performance.id}`
  const icon = (!areTracksFetched || !isPlaying) ? 'play' : 'pause'


  const shouldAnimateNow = animate && !didRunRef.current

  useEffect(() => {
    if (animate) didRunRef.current = true
  }, [animate])



  const fetchAndEnqueue = useCallback(async () => {
    const url = `/performances/${performance.id}/tracks`
    const resp = await getAndQueueTracks(url, performance.id, 'performance')
    
    if (resp === 'error') {
      showAlert('Error loading tracks', 'error')
      return
    }

    // Load Apple Music
    const amUrl = `/services/apple-music/performances/${performance.id}/tracks`
    const amResp = await show(amUrl)
    const data = amResp.data
    const converted = convertAppleMusicTracksToCkordFormat(data, performance.id, 'Event')
    const shuffled = shuffle(converted)
    dispatch({type:'TRACKS_INDEX_SUCCESS', payload:{data:shuffled}})
    enqueue(shuffled, performance.id, 'performance')
    addTracksToPerformance(performance.id, shuffled)

    // Load Spotify
    // const spUrl = `/services/spotify/performances/${performance.id}/tracks`
    // const spResp = await show(spUrl).catch(() => null)
    // if (spResp?.data) {
    //   const convertedSP = convertSpotifyTracksToCkordFormat(spResp.data, performance.id, 'Event')
    //   const shuffledSP = shuffle(convertedSP)
    //   dispatch({type: 'TRACKS_INDEX_SUCCESS', payload: {data: shuffledSP}})
    //   enqueue(shuffledSP, performance.id, 'performance')
    //   addTracksToPerformance(performance.id, shuffledSP)
    // }
  }, [performance.id, getAndQueueTracks, enqueue, addTracksToPerformance, showAlert, dispatch])



  const handleButtonPress = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    if (icon === 'play') {
      areTracksFetched ? queue(tracks, performance.id) : fetchAndEnqueue()
    } else {
      pause()
    }
  }, [icon, areTracksFetched, tracks, performance.id, fetchAndEnqueue, queue, pause])


  const handleClick = useCallback(e => {
    e.preventDefault()
    navigate(href)
  }, [navigate, href])




  return (
    <Link css={styles.link} to={href}>
      <div 
        onClick={handleClick}
        css={[styles.root, shouldAnimateNow && styles.fadeIn]}
      >
        <div ref={containerRef} css={styles.innerRoot}>
          <div css={styles.dateContainer}>
            <MapListItemDate date={start} performanceId={performance.id} />
          </div>

          <div css={styles.imageContainer}>
            <img
              css={styles.image}
              src={imageURL || externalImageURL}
              srcSet={externalImageSrcset}
              alt={performance.attributes?.name}
            />

            {trackCount > 0 &&
              <div css={styles.iconContainer}>
                <div css={styles.iconSubContainer}>
                  <div css={styles.iconCircle} onClick={handleButtonPress}>
                    {icons[icon]}
                  </div>
                </div>
              </div>
            }
          </div>

          <div css={styles.info}>
            <MapListItemInformation
              performance={performance} 
              containerRef={containerRef}
            />
          </div>
        </div>
      </div>
    </Link>
  )
}



const makeMapStateToProps = () => {
  const getPerformanceById = makeGetPerformanceById()
  const getPerformanceTracks = makeGetPerformanceTracks()
  
  const mapStateToProps = (state, props) => {  
    const id = props.performanceId
    
    const performance = getPerformanceById(state, id) || {}

    const tracks = getPerformanceTracks(state, id)
    
    const isCaller = state.player.callerID === id
    
    const isPlaying = state.player.isPlayRequested && isCaller

    return {
      start: performance.attributes?.start,
      tracks: tracks,
      trackCount: performance.attributes?.trackCount + performance.attributes?.appleMusicLinks.length,
      isCaller: isCaller,
      isClicked: state.map.selectedPerformance === id,
      isPlaying: isPlaying,
      performance: performance,
      areTracksFetched: selectPerformanceFetchedAssociations (state, id).includes('tracks'),
      imageURL: performance.attributes?.imageDerivatives.small || performance.attributes?.imageDerivatives.original,
      externalImageURL: performance.attributes?.externalImageUrl,
      externalImageSrcset: performance.attributes?.externalImageSrcset
    }
  }

  return mapStateToProps
}


const actions = { 
  play, 
  pause, 
  queue,
  enqueue,
  showAlert,
  getAndQueueTracks,
  addTracksToPerformance
}


export default connect(makeMapStateToProps, actions)(MapListItem)


